<template>
  <div v-if="mounted" class="mobile-report">
    <header class="mobile-report__header">
      <button class="mobile-report__back-button" @click="goBack">
        ← Voltar
      </button>
      <div class="mobile-report__header-content">
        <h1 class="mobile-report__title">Relatório de Satisfação</h1>
        <p class="mobile-report__customer">
          {{ reports.customer.company_name }} - 
          {{ this.capitalize(this.$moment().format("MMMM[/]YYYY")) }}
        </p>
      </div>
    </header>

    <div class="mobile-report__content">
      <!-- Summary Cards -->
      <div class="mobile-report__summary">
        <div class="mobile-report__summary-card">
          <div class="mobile-report__summary-icon">
            <i class="fas fa-chart-bar"></i>
          </div>
          <div class="mobile-report__summary-info">
            <h3 class="mobile-report__summary-title">Total de avaliações</h3>
            <p class="mobile-report__summary-value">{{ totalEvaluations }}</p>
          </div>
        </div>
        
        <div class="mobile-report__summary-card">
          <div class="mobile-report__summary-icon">
            <i class="fas fa-question-circle"></i>
          </div>
          <div class="mobile-report__summary-info">
            <h3 class="mobile-report__summary-title">Total de perguntas</h3>
            <p class="mobile-report__summary-value">{{ reports.questions.length }}</p>
          </div>
        </div>
      </div>

      <!-- Customer Logo -->
      <div class="mobile-report__logo-section" v-if="reports.customer.logo">
        <img 
          :src="reports.customer.logo" 
          class="mobile-report__customer-logo"
          alt="Logo do cliente"
        />
      </div>

      <!-- Chart Tabs -->
      <div class="mobile-report__tabs">
        <button 
          class="mobile-report__tab"
          :class="{ 'mobile-report__tab--active': activeTab === 'overview' }"
          @click="activeTab = 'overview'"
        >
          Visão Geral
        </button>
        <button 
          class="mobile-report__tab"
          :class="{ 'mobile-report__tab--active': activeTab === 'questions' }"
          @click="activeTab = 'questions'"
        >
          Perguntas
        </button>
        <button 
          class="mobile-report__tab"
          :class="{ 'mobile-report__tab--active': activeTab === 'comments' }"
          @click="activeTab = 'comments'"
        >
          Comentários
        </button>
      </div>

      <!-- Overview Tab -->
      <div v-show="activeTab === 'overview'" class="mobile-report__tab-content">
        <div class="mobile-report__chart-container">
          <h2 class="mobile-report__chart-title">Evolução das avaliações</h2>
          <BarChart 
            :chart-data="totalEvaluationsChartData" 
            :options="chartOptions"
            class="mobile-report__chart"
          />
        </div>
      </div>

      <!-- Questions Tab -->
      <div v-show="activeTab === 'questions'" class="mobile-report__tab-content">
        <div 
          class="mobile-report__question-card"
          v-for="question in chartQuestions"
          :key="question.uuid"
        >
          <h3 class="mobile-report__question-title">{{ question.question }}</h3>
          <div class="mobile-report__chart-container mobile-report__chart-container--small">
            <BarChart 
              :chart-data="getQuestionChartData(question)" 
              :options="chartOptions"
              class="mobile-report__chart"
            />
          </div>
        </div>
      </div>

      <!-- Comments Tab -->
      <div v-show="activeTab === 'comments'" class="mobile-report__tab-content">
        <div 
          class="mobile-report__comment-card"
          v-for="question in textQuestions"
          :key="question.uuid"
        >
          <h3 class="mobile-report__question-title">{{ question.question }}</h3>
          <div class="mobile-report__comments">
            <div 
              class="mobile-report__comment"
              v-for="quote in question.responses"
              :key="quote.uuid"
            >
              <p class="mobile-report__comment-text">"{{ quote.response }}"</p>
              <p class="mobile-report__comment-author">
                {{ quote.name || "Sem Identificação" }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="mobile-report__footer">
      <p class="mobile-report__footer-text">
        Mi.Conta agradece sua atenção e estamos à disposição para lhe ajudar 
        no que se refere à interpretação dos dados e auxiliá-lo nas tomadas de decisões.
      </p>
      <div class="mobile-report__quote-section">
        <p class="mobile-report__quote-miconta">
          "A melhor propaganda é feita por Clientes satisfeitos"
        </p>
        <p class="mobile-report__quote-author-miconta">Philip Kotler</p>
      </div>
      <img 
        src="/img/miconta-logo-landscape.png" 
        class="mobile-report__footer-logo"
        alt="Logo Mi.Conta"
      />
    </div>
  </div>

  <div class="mobile-report__loading" v-else>
    <div class="mobile-report__spinner"></div>
    <p class="mobile-report__loading-text">Buscando relatório...</p>
  </div>
</template>

<script>
import { mapState } from "vuex";
import BarChart from "@/components/BarChart.vue";

const typesByGroupMonth = [
  "BOOLEAN",
  "CHOICES",
  "CHECK_BOXES",
  "LINEAR_SCALE",
  "LINER_SCALE", // Typo from backend
  "DROPDOWN"
];

export default {
  name: "MobileReport",
  components: { BarChart },
  data: () => ({
    mounted: false,
    reports: {},
    activeTab: "overview"
  }),
  props: {
    uuid: {
      type: String,
      default: ""
    }
  },
  async mounted() {
    await this.getReportApiAsnc();
    setTimeout(() => {
      this.mounted = true;
    }, 500);
  },
  computed: {
    ...mapState("User", ["token"]),
    totalEvaluations() {
      return Object.values(this.reports.answers_by_month).reduce((sum, count) => sum + count, 0);
    },
    totalEvaluationsChartData() {
      const labels = Object.keys(this.reports.answers_by_month).map(month => 
        this.capitalize(month)
      );
      
      const data = Object.values(this.reports.answers_by_month);
      
      return {
        labels,
        datasets: [
          {
            label: "Total de avaliações",
            backgroundColor: "#42b983",
            data
          }
        ]
      };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true
            }
          }]
        }
      };
    },
    chartQuestions() {
      return this.reports.questions.filter(question => this.isChartPage(question) && 
        Object.keys(question.grouped_by_month).length > 0);
    },
    textQuestions() {
      return this.reports.questions.filter(question => !this.isChartPage(question) && 
        question.responses.length > 0);
    }
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },
    async getReportApiAsnc() {
      try {
        const response = await this.$http.get(`reports/export/${this.uuid}`, {
          headers: {
            Authorization: this.token
          }
        });

        this.reports = response.body.data;
      } catch (err) {
        console.error(err);
      }
    },
    capitalize(string) {
      if (typeof string !== "string") return "";
      const newString = string.toLowerCase();
      return newString.charAt(0).toUpperCase() + newString.slice(1);
    },
    isChartPage(question) {
      // Handle both correct and typo versions of LINEAR_SCALE
      const type = question.type_response;
      return typesByGroupMonth.includes(type) || type === "LINER_SCALE";
    },
    getQuestionChartData(question) {
      // Get all unique months
      const allMonths = Object.keys(question.grouped_by_month);
      
      let responseLabels, filteredData;
      
      // For LINEAR_SCALE questions, filter out responses with zero values
      if (question.type_response === "LINEAR_SCALE" || question.type_response === "LINER_SCALE") {
        // Find which responses have non-zero values across all months
        const nonZeroIndices = question.responses.reduce((indices, response, index) => {
          const hasNonZero = allMonths.some(month => {
            const monthData = question.grouped_by_month[month];
            return monthData && monthData[index] > 0;
          });
          if (hasNonZero) {
            indices.push(index);
          }
          return indices;
        }, []);
        
        // Filter responses and data based on non-zero indices
        responseLabels = nonZeroIndices.map(index => String(question.responses[index]));
        filteredData = nonZeroIndices.map((index, filteredIndex) => {
          const colors = [
  "#c0392b",
  "#d35400",
  "#e67e22",
  "#f1c40f",
  "#cddc39",
  "#9ccc65",
  "#66bb6a",
  "#43a047",
  "#2e7d32",
  "#1b5e20"
];
          
          return {
            label: responseLabels[filteredIndex],
            backgroundColor: colors[filteredIndex % colors.length],
            data: allMonths.map(month => {
              const monthData = question.grouped_by_month[month];
              return monthData ? monthData[index] || 0 : 0;
            })
          };
        });
      } if (question.type_response == "BOOLEAN") {
         responseLabels = question.responses.map(response => String(response));
        filteredData = responseLabels.map((response, index) => {
          const colors = [
  "#66bb6a",
  "#e67e22",
];
          
          return {
            label: response,
            backgroundColor: colors[index % colors.length],
            data: allMonths.map(month => {
              const monthData = question.grouped_by_month[month];
              return monthData ? monthData[index] || 0 : 0;
            })
          };
        });
      } else {
        // For other question types, use all responses
        responseLabels = question.responses.map(response => String(response));
        filteredData = responseLabels.map((response, index) => {
          const colors = [
  "#c0392b",
  "#e67e22",
  "#cddc39",
  "#66bb6a",
  "#1b5e20"
];
          
          return {
            label: response,
            backgroundColor: colors[index % colors.length],
            data: allMonths.map(month => {
              const monthData = question.grouped_by_month[month];
              return monthData ? monthData[index] || 0 : 0;
            })
          };
        });
      }
      
      return {
        labels: allMonths.map(month => this.capitalize(month)),
        datasets: filteredData
      };
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/components/variables";

.mobile-report {
  background-color: #f8f9fa;
  min-height: 100vh;
  padding-bottom: 24px;
  
  &__header {
    background: linear-gradient(135deg, #323639 0%, #23272b 100%);
    color: white;
    padding: 16px;
    position: relative;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  }
  
  &__back-button {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: white;
    font-size: 1rem;
    cursor: pointer;
    padding: 8px 12px;
    border-radius: 4px;
    z-index: 10;
    
    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }
  }
  
  &__header-content {
    text-align: center;
    padding: 0 40px;
  }
  
  &__title {
    margin: 0 0 8px;
    font-size: 1.5rem;
    font-weight: bold;
  }
  
  &__customer {
    margin: 0;
    font-size: 1rem;
    opacity: 0.9;
  }
  
  &__content {
    padding: 16px;
  }
  
  &__summary {
    display: flex;
    gap: 16px;
    margin-bottom: 24px;
    flex-wrap: wrap;
  }
  
  &__summary-card {
    flex: 1;
    min-width: 140px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    padding: 16px;
    display: flex;
    align-items: center;
  }
  
  &__summary-icon {
    width: 48px;
    height: 48px;
    background: #e8f5e9;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
    color: #42b983;
    font-size: 1.2rem;
  }
  
  &__summary-info {
    flex: 1;
  }
  
  &__summary-title {
    margin: 0 0 4px;
    font-size: 0.9rem;
    color: #666;
    font-weight: normal;
  }
  
  &__summary-value {
    margin: 0;
    font-size: 1.5rem;
    font-weight: bold;
    color: #333;
  }
  
  &__logo-section {
    text-align: center;
    margin-bottom: 24px;
  }
  
  &__customer-logo {
    max-width: 200px;
    max-height: 120px;
    object-fit: contain;
  }
  
  &__tabs {
    display: flex;
    background: white;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    margin-bottom: 24px;
  }
  
  &__tab {
    flex: 1;
    padding: 16px 8px;
    border: none;
    background: transparent;
    font-size: 0.9rem;
    font-weight: 600;
    color: #666;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &--active {
      color: #42b983;
      border-bottom: 3px solid #42b983;
    }
  }
  
  &__tab-content {
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    padding: 16px;
    margin-bottom: 24px;
  }
  
  &__chart-title {
    margin: 0 0 16px;
    font-size: 1.25rem;
    color: #333;
  }
  
  &__chart-container {
    height: 300px;
    position: relative;
    margin-bottom: 16px;
    
    &--small {
      height: 200px;
    }
  }
  
  &__chart {
    height: 100%;
  }
  
  &__question-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    padding: 16px;
    margin-bottom: 16px;
  }
  
  &__question-title {
    margin: 0 0 16px;
    font-size: 1.1rem;
    color: #333;
  }
  
  &__comment-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    padding: 16px;
    margin-bottom: 16px;
  }
  
  &__comments {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  
  &__comment {
    background-color: #f8f9fa;
    border-left: 4px solid #42b983;
    padding: 12px;
    border-radius: 4px;
  }
  
  &__comment-text {
    margin: 0 0 8px;
    font-style: italic;
    color: #444;
  }
  
  &__comment-author {
    margin: 0;
    font-weight: bold;
    color: #666;
    text-align: right;
    font-size: 0.9rem;
  }
  
  &__footer {
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    padding: 24px 16px;
    text-align: center;
    margin: 0 16px 24px;
  }
  
  &__footer-text {
    color: #555;
    line-height: 1.6;
    margin: 0 0 24px;
  }
  
  &__quote-section {
    background-color: #f0f7f1;
    padding: 16px;
    border-radius: 8px;
    margin: 0 0 24px;
  }
  
  &__quote-miconta {
    font-style: italic;
    margin: 0 0 8px;
    color: #333;
  }
  
  &__quote-author-miconta {
    margin: 0;
    font-weight: bold;
    color: #555;
  }
  
  &__footer-logo {
    max-width: 150px;
    opacity: 0.7;
    margin: 0 auto;
  }
  
  &__loading {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100vh;
    background-color: #f8f9fa;
  }
  
  &__spinner {
    width: 40px;
    height: 40px;
    border: 4px solid rgba(0, 0, 0, 0.1);
    border-radius: 50%;
    border-top-color: #42b983;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }
  
  &__loading-text {
    margin: 0;
    color: #666;
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
}

@media (max-width: 480px) {
  .mobile-report {
    &__title {
      font-size: 1.25rem;
    }
    
    &__summary {
      flex-direction: column;
    }
    
    &__summary-card {
      width: 100%;
    }
    
    &__chart-container {
      height: 250px;
      
      &--small {
        height: 180px;
      }
    }
  }
}
</style>